import { useState, useRef, useEffect } from "react";
import { api, formatErr } from "@/lib/api";
import { Video as VideoIcon, Upload, Trash2, X, Loader2, GripVertical, Play } from "lucide-react";

const MAX_VIDEOS = 10;
const MAX_BYTES = 50 * 1024 * 1024; // 50MB
const ACCEPT = "video/mp4,video/webm,video/quicktime,video/x-m4v";

/**
 * Profile video reel — upload up to 10 short clips of your work/life.
 * Editable: shown to the profile owner (in EditProfile). Read-only display is `<VideoReelView />`.
 */
export default function VideoReel({ initialVideos = [], onChange }) {
  const [videos, setVideos] = useState(initialVideos || []);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [progress, setProgress] = useState(0);
  const [caption, setCaption] = useState("");
  const [openIdx, setOpenIdx] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => { setVideos(initialVideos || []); }, [initialVideos]);

  const pick = () => inputRef.current?.click();

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!ACCEPT.split(",").includes(f.type)) {
      setErr("Unsupported file type. Use mp4, webm, or mov."); return;
    }
    if (f.size > MAX_BYTES) {
      setErr("Video too large (max 50MB)."); return;
    }
    if (videos.length >= MAX_VIDEOS) {
      setErr(`Maximum of ${MAX_VIDEOS} videos. Remove one first.`); return;
    }
    setErr(""); setBusy(true); setProgress(0);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data: up } = await api.post("/uploads/video", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) => {
          if (evt.total) setProgress(Math.round((evt.loaded / evt.total) * 100));
        },
      });
      const { data: v } = await api.post("/me/videos", { url: up.url, caption });
      const next = [v, ...videos];
      setVideos(next);
      onChange?.(next);
      setCaption("");
    } catch (e2) { setErr(formatErr(e2)); }
    finally { setBusy(false); setProgress(0); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this video? This can't be undone.")) return;
    setErr("");
    try {
      await api.delete(`/me/videos/${id}`);
      const next = videos.filter(v => v.id !== id);
      setVideos(next);
      onChange?.(next);
    } catch (e) { setErr(formatErr(e)); }
  };

  return (
    <div className="card p-5 sm:p-6" data-testid="video-reel">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div>
          <p className="overline mb-1">Your reel · {videos.length}/{MAX_VIDEOS}</p>
          <p className="text-sm text-[#4A4A4A]">Show people the work, the craft, the day-to-day. Up to {MAX_VIDEOS} short clips, 50MB each.</p>
        </div>
        <button type="button" onClick={pick} disabled={busy || videos.length >= MAX_VIDEOS}
                className="btn-primary !py-2 !px-3 text-sm" data-testid="video-upload-btn">
          {busy ? <><Loader2 size={14} className="animate-spin"/> Uploading… {progress}%</> : <><Upload size={14}/> Upload clip</>}
        </button>
        <input ref={inputRef} type="file" accept={ACCEPT} className="hidden"
               onChange={handleFile} data-testid="video-file-input"/>
      </div>

      <input
        type="text"
        value={caption}
        onChange={(e)=>setCaption(e.target.value)}
        placeholder="Optional caption for the next clip (max 240 chars)"
        maxLength={240}
        className="input"
        data-testid="video-caption"
      />

      {err && <p className="text-sm text-[#B84B4B] mt-3" data-testid="video-error">{err}</p>}

      {videos.length === 0 ? (
        <div className="mt-5 border border-dashed border-[#E5E5E3] rounded-2xl p-10 text-center" data-testid="video-empty">
          <VideoIcon size={22} className="mx-auto text-[#0057FF] mb-2"/>
          <p className="text-sm text-[#4A4A4A]">No videos yet. Drop in your first reel — beach session, studio time, finished work.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-5" data-testid="video-grid">
          {videos.map((v, i) => (
            <div key={v.id} className="relative group rounded-2xl overflow-hidden bg-[#0A0A0A] aspect-[9/12]" data-testid={`video-tile-${v.id}`}>
              <button type="button" onClick={()=>setOpenIdx(i)} className="w-full h-full">
                {v.thumbnail
                  ? <img src={v.thumbnail} alt="" className="w-full h-full object-cover"/>
                  : (
                    // No uploaded cover — grab the first frame ourselves.
                    // preload="auto" forces iOS Safari to fetch + decode the
                    // initial frame; a tiny seek nudges the browser to paint it.
                    <video
                      src={v.url}
                      className="w-full h-full object-cover bg-[#111]"
                      preload="auto"
                      muted
                      playsInline
                      onLoadedMetadata={(e)=>{ try { e.currentTarget.currentTime = 0.1; } catch { /* noop */ } }}
                    />
                  )
                }
                <span className="absolute inset-0 flex items-center justify-center bg-black/20 opacity-100 group-hover:opacity-0 transition-opacity">
                  <Play size={28} className="text-white drop-shadow"/>
                </span>
              </button>
              {v.caption && (
                <p className="absolute bottom-0 inset-x-0 p-2 text-xs text-white bg-gradient-to-t from-black/70 to-transparent line-clamp-2">{v.caption}</p>
              )}
              <div className="absolute top-2 left-2 inline-flex items-center gap-0.5 text-white/70" title="Reorder coming soon">
                <GripVertical size={12}/>
              </div>
              <button type="button" onClick={()=>remove(v.id)}
                      className="absolute top-2 right-2 h-7 w-7 rounded-full bg-black/60 text-white inline-flex items-center justify-center hover:bg-[#B84B4B] transition-colors"
                      data-testid={`video-delete-${v.id}`}
                      title="Delete">
                <Trash2 size={13}/>
              </button>
            </div>
          ))}
        </div>
      )}

      {openIdx !== null && videos[openIdx] && (
        <VideoLightbox
          videos={videos}
          startIdx={openIdx}
          onClose={()=>setOpenIdx(null)}
        />
      )}
    </div>
  );
}

/** Public read-only display used on a Hooman's profile page. */
export function VideoReelView({ videos = [] }) {
  const [openIdx, setOpenIdx] = useState(null);
  if (!videos?.length) return null;
  return (
    <div data-testid="video-reel-view">
      <p className="overline mb-4">Reel · {videos.length}</p>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="video-reel-grid">
        {videos.map((v, i) => (
          <button key={v.id} type="button" onClick={()=>setOpenIdx(i)}
                  data-testid={`reel-tile-${v.id}`}
                  className="relative group rounded-2xl overflow-hidden bg-[#0A0A0A] aspect-[9/12]">
            {v.thumbnail
              ? <img src={v.thumbnail} alt="" className="w-full h-full object-cover"/>
              : (
                <video
                  src={v.url}
                  className="w-full h-full object-cover bg-[#111]"
                  preload="auto"
                  muted
                  playsInline
                  onLoadedMetadata={(e)=>{ try { e.currentTarget.currentTime = 0.1; } catch { /* noop */ } }}
                />
              )
            }
            <span className="absolute inset-0 flex items-center justify-center bg-black/15 group-hover:bg-black/0 transition-colors">
              <Play size={26} className="text-white drop-shadow"/>
            </span>
            {v.caption && (
              <p className="absolute bottom-0 inset-x-0 p-2 text-xs text-white bg-gradient-to-t from-black/70 to-transparent line-clamp-2">{v.caption}</p>
            )}
          </button>
        ))}
      </div>
      {openIdx !== null && videos[openIdx] && (
        <VideoLightbox videos={videos} startIdx={openIdx} onClose={()=>setOpenIdx(null)}/>
      )}
    </div>
  );
}

function VideoLightbox({ videos, startIdx, onClose }) {
  const [idx, setIdx] = useState(startIdx);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") setIdx(i => Math.min(videos.length - 1, i + 1));
      if (e.key === "ArrowLeft") setIdx(i => Math.max(0, i - 1));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [videos.length, onClose]);
  const v = videos[idx];
  return (
    <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
         onClick={onClose} data-testid="video-lightbox">
      <button type="button" onClick={onClose}
              className="absolute top-4 right-4 text-white/80 hover:text-white"
              data-testid="video-lightbox-close"><X size={22}/></button>
      <div onClick={(e)=>e.stopPropagation()} className="max-w-2xl w-full">
        <video src={v.url} controls autoPlay playsInline className="w-full rounded-2xl bg-black"/>
        {v.caption && <p className="text-white text-sm mt-3 text-center">{v.caption}</p>}
        <p className="text-white/50 text-xs mt-2 text-center">{idx + 1} / {videos.length}</p>
      </div>
    </div>
  );
}
